import { useEffect, useState } from 'react';
import { SubPage, SubPageOption } from '../../../../contexts/PagesContext';
import ModalComponent from '../../../../components/ModalComponent';

interface SubpageCreateEditModalProps {
  open: boolean;
  closeModal: (close: boolean) => void;
  options: SubPageOption[];
  domain: string;
  setNewSubpage: (subpage: SubPage) => void;
  subpage?: SubPage;
  subpages?: SubPage[];
}

function SubpageCreateEditModal({
  open: modificationModalOpen,
  closeModal: setModificationModalOpen,
  options: incomingOptions,
  domain,
  subpage: subdirectory,
  subpages: subdirectories,
  setNewSubpage,
}: SubpageCreateEditModalProps) {
  const [subpage, setSubpage] = useState<SubPage | null>(subdirectory || {
    id: null,
    url_path: '',
    subpage_id: null,
    subpage_title: '',
    subpage_url: '',
    is_subpage_published: false,
  });
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [options, setOptions] = useState<SubPageOption[]>([]);

  const handleOptionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOption = options.find((option: SubPageOption) => option.id === e.target.value);
    setSubpage({
      ...subpage,
      subpage_id: selectedOption.id,
      subpage_title: selectedOption.title,
      subpage_url: selectedOption.url_path,
      is_subpage_published: selectedOption.is_published,
    });
  };

  const checkIfUrlPathIsNotContainedInSubpages = (urlPath: string) => {
    if (!subdirectories || subdirectories.length < 0) {
      return true;
    }

    const filteredSubpages = subdirectories.filter((subdir: SubPage) => !subdir?.deleted);
    const isNotContained = filteredSubpages.every((subdir: SubPage) => subdir.url_path !== urlPath || subdir.subpage_id === subpage.subpage_id);
    return isNotContained;
  };

  const handleAddPage = () => {
    // verify url path
    if (!checkIfUrlPathIsNotContainedInSubpages(subpage.url_path)) {
      setErrorMessage('Subdirectory name is duplicated');
      return;
    }

    // add page
    if (!subpage.subpage_id) {
      setNewSubpage({
        ...subpage,
        subpage_id: options[0].id,
        subpage_title: options[0].title,
        subpage_url: options[0].url_path,
        is_subpage_published: options[0].is_published,
      });
      setModificationModalOpen(false);
      return;
    }

    setNewSubpage({ ...subpage });
    setModificationModalOpen(false);
  };

  const validateUrlPath = (urlPath: string) => {
    if (urlPath.length < 1) {
      return { valid: true, error: '' };
    }

    // Check for invalid characters first
    const charRegex = /^[a-zA-Z0-9/_-]+$/;
    if (!charRegex.test(urlPath)) {
      return { valid: false, error: 'invalid_chars' };
    }

    // Check for slash at start or end
    if (urlPath.startsWith('/') || urlPath.endsWith('/')) {
      return { valid: false, error: 'invalid_slash_position' };
    }

    // Check for consecutive slashes
    if (urlPath.includes('//')) {
      return { valid: false, error: 'consecutive_slashes' };
    }

    // Check hierarchy levels (maximum 4 slashes = 5 levels)
    const slashCount = (urlPath.match(/\//g) || []).length;
    if (slashCount > 4) {
      return { valid: false, error: 'max_levels' };
    }

    // Check if each segment is valid (not empty)
    const segments = urlPath.split('/');
    if (segments.some((segment) => segment.length === 0)) {
      return { valid: false, error: 'empty_segment' };
    }

    return { valid: true, error: '' };
  };

  const handleSubpageUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length > 255) {
      setErrorMessage('Subdirectory name must be 255 characters or fewer');
      return;
    }

    const validation = validateUrlPath(e.target.value);
    if (!validation.valid) {
      switch (validation.error) {
        case 'invalid_chars':
          setErrorMessage('Subdirectory name can only contain alphanumeric characters and "- _ /"');
          break;
        case 'invalid_slash_position':
          setErrorMessage('Subdirectory name cannot start or end with "/"');
          break;
        case 'consecutive_slashes':
          setErrorMessage('Consecutive "//" are not allowed');
          break;
        case 'max_levels':
          setErrorMessage('Subdirectories can have a maximum of 5 levels');
          break;
        case 'empty_segment':
          setErrorMessage('Empty path segments are not allowed');
          break;
        default:
          setErrorMessage('Invalid subdirectory name');
      }
      return;
    }

    setErrorMessage('');
    setSubpage({ ...subpage, url_path: e.target.value });
  };

  const handleCloseModal = () => {
    setModificationModalOpen(false);
  };

  useEffect(() => {
    setOptions(incomingOptions);
  }, [incomingOptions, options]);

  return (
    <>
      <ModalComponent
        show={modificationModalOpen}
        onConfirm={handleAddPage}
        closeModal={handleCloseModal}
        title="Edit page"
        confirmButtonText="Update"
        cancelButtonColor="outline-secondary"
        backdrop="static"
        backdropClassName="modal-backdrop fade show"
        centered
        disabled={
          !subpage.url_path
          || options?.length < 0 || !options
          || errorMessage !== ''
        }
      >
        <>
          <div className="">
            <label className="col-form-label text-sm-start">
              Subdirectory name
              <span>
                *
              </span>
            </label>
            <div className="input-group">
              <label className="input-group-text" htmlFor="subdirectory">
                https://
                {domain}
                /
              </label>
              <input
                id="subdirectory"
                name="subdirectory"
                type="text"
                className="form-control"
                placeholder="Enter subdirectory name"
                onChange={handleSubpageUrlChange}
                defaultValue={subpage.url_path}
              />
              <label className="input-group-text" htmlFor="subdirectory">/</label>
            </div>
            {errorMessage && (
              <div className="text-danger">
                {errorMessage}
              </div>
            )}
          </div>
          <div className="my-3">
            <label className="form-label">Select page</label>
            <select
              onChange={handleOptionChange}
              className="form-select"
              value={subpage.subpage_id || (options?.length > 0 ? options[0].id : 0)}
            >
              {options?.length > 0 ? (
                options.map((option: SubPageOption) => (
                  <option
                    key={option.id}
                    value={option.id}
                    translate="no"
                    className="notranslate"
                  >
                    https://
                    {option.url_path}
                    /
                    (
                    {option.title}
                    )
                  </option>
                ))
              ) : (
                <option value={-1}>No pages available</option>
              )}
            </select>
          </div>
        </>
      </ModalComponent>
    </>
  );
}

export default SubpageCreateEditModal;
